import { usePowerSyncChange } from '@/renderer/lib/powersync/ipcBridge';
import { localFetchOpenDineOrderTableIds } from '@/renderer/lib/powersync/localOrderOps';
import { syncEvents } from '@/renderer/lib/powersync/supabaseConnector';
import { supabase } from '@/renderer/services/supabase';
import { apiError, apiInfo } from '@/renderer/util/notify';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { selectOutletId } from '@store/auth/authSelectors';
import {
  fetchTakeAwayOrdersForOutlet,
  refreshSelectedOrderContext,
  selectDiningTableOrder,
  selectSelectedOrder,
  selectSelectedTakeAwayId,
} from '@store/runningOrder';
import { setLocalOpenDineTableIds } from '@store/sections/sectionSlice';
import { fetchTables } from '@store/sections/sectionThunks';
import React, { useCallback, useEffect } from 'react';

export const RunningOrderLiveSync: React.FC = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectOutletId);
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const selectedTakeAwayId = useAppSelector(selectSelectedTakeAwayId);
  const selectedOrderId = selectedOrder?.order_id;

  // Sync local open DINE order table IDs from SQLite so the table grid
  // highlights occupied tables even for offline-created orders that haven't
  // uploaded to Supabase yet.
  const syncLocalTableIds = useCallback(async () => {
    if (!outletId) return;
    const tableIds = await localFetchOpenDineOrderTableIds(outletId);
    dispatch(setLocalOpenDineTableIds(tableIds));
  }, [dispatch, outletId]);

  // Populate on mount.
  useEffect(() => {
    syncLocalTableIds();
  }, [syncLocalTableIds]);

  // Re-populate whenever any order row changes in local SQLite (PowerSync write
  // or sync download). This keeps highlighted tables in sync with local state.
  usePowerSyncChange(['orders'], () => {
    syncLocalTableIds();
  });

  // Item shifts (shiftSelectedConfirmedOrderItem) are a Category B write
  // straight to Supabase — the local SQLite mirror for order_items only
  // catches up once PowerSync's downstream sync pulls the row's changed
  // order_id down. The Realtime order_items subscription below is filtered
  // by the SOURCE order's id, which never matches post-shift (the row's
  // order_id changed AWAY from that filter), so it can't self-correct this
  // case. This listener isn't order-scoped, so it fires regardless of
  // which direction order_id changed — self-heals the currently-selected
  // order's item list whenever local SQLite does catch up, as a backstop
  // in case the thunk's own optimistic removal was ever wrong (e.g. a
  // concurrent edit raced it).
  usePowerSyncChange(['order_items'], () => {
    if (selectedOrderId) {
      dispatch(refreshSelectedOrderContext());
    }
  });

  // Show an error toast when the PowerSync connector detects a cross-device
  // bill_number conflict (23505) and has cleaned up the ghost order locally.
  useEffect(() => {
    const handler = (e: Event) => {
      const { message } = (e as CustomEvent<{ message: string }>).detail;
      apiError(message);
    };
    syncEvents.addEventListener('bill-conflict', handler);
    return () => syncEvents.removeEventListener('bill-conflict', handler);
  }, []);

  // Show an error toast whenever any other unsynced change is permanently
  // discarded (fatal Postgres error, or repeated failures exhausting the
  // retry ceiling) so the user knows to redo it if it still matters, instead
  // of it silently vanishing.
  useEffect(() => {
    const handler = (e: Event) => {
      const { message } = (e as CustomEvent<{ message: string }>).detail;
      apiError(message);
    };
    syncEvents.addEventListener('sync-discarded', handler);
    return () => syncEvents.removeEventListener('sync-discarded', handler);
  }, []);

  // Uploads are held because the company is disabled or its subscription is
  // paused. Nothing is lost — local orders upload once it's reactivated. The
  // connector throttles this event, so it won't spam on every retry.
  useEffect(() => {
    const handler = (e: Event) => {
      const { message } = (e as CustomEvent<{ message: string }>).detail;
      apiError(message);
    };
    syncEvents.addEventListener('sync-paused', handler);
    return () => syncEvents.removeEventListener('sync-paused', handler);
  }, []);

  // A cross-device bill_number collision was resolved by renumbering the
  // order instead of deleting it (see supabaseConnector.ts). The local row
  // is already updated — just let staff know so they can reprint if needed.
  useEffect(() => {
    const handler = (e: Event) => {
      const { newBillNumber } = (
        e as CustomEvent<{ orderId: string; newBillNumber: number }>
      ).detail;
      apiInfo(`Bill renumbered to #${newBillNumber} — please reprint.`);
      dispatch(refreshSelectedOrderContext());
    };
    syncEvents.addEventListener('bill-renumbered', handler);
    return () => syncEvents.removeEventListener('bill-renumbered', handler);
  }, [dispatch]);

  // Two devices opened an order for the same table while both offline — the
  // losing order's items were merged onto the surviving order instead of
  // being lost (see supabaseConnector.ts). If this device had the
  // now-deleted losing order open, its table/takeaway lists are stale;
  // refresh them so the merged order shows up under its surviving id.
  useEffect(() => {
    const handler = (e: Event) => {
      const { losingOrderId, tableId } = (
        e as CustomEvent<{
          losingOrderId: string;
          winningOrderId: string;
          tableId?: number;
        }>
      ).detail;
      apiInfo(
        tableId
          ? `Your item(s) were added to the existing order for Table ${tableId}.`
          : 'Your order was merged with an existing order created on another device.',
      );
      if (outletId) {
        dispatch(fetchTables(outletId));
      }
      if (selectedOrder?.order_id === losingOrderId) {
        syncLocalTableIds();
        // This device was actively viewing the order that just got merged
        // away — re-select the table so it loads the surviving order (with
        // all merged items) instead of continuing to show the now-deleted
        // losing order.
        dispatch(
          selectDiningTableOrder({
            id: selectedOrder.table_id,
            name: selectedOrder.table_name,
            sectionId: selectedOrder.section_id,
            sectionName: selectedOrder.section_name,
          }),
        );
      }
    };
    syncEvents.addEventListener('table-order-merged', handler);
    return () => syncEvents.removeEventListener('table-order-merged', handler);
  }, [dispatch, outletId, selectedOrder, syncLocalTableIds]);

  useEffect(() => {
    if (!selectedOrderId) return undefined;

    let disposed = false;
    let refreshTimeout: number | null = null;
    const channelName = `running-order-sync-${selectedOrderId}`;

    const queueRefresh = () => {
      if (disposed) return;
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }
      refreshTimeout = window.setTimeout(() => {
        dispatch(refreshSelectedOrderContext());
      }, 120);
    };

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${selectedOrderId}`,
        },
        queueRefresh,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_items',
          filter: `order_id=eq.${selectedOrderId}`,
        },
        queueRefresh,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'discounts',
          filter: `order_id=eq.${selectedOrderId}`,
        },
        queueRefresh,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_settlements',
          filter: `order_id=eq.${selectedOrderId}`,
        },
        queueRefresh,
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          queueRefresh();
        }
      });

    return () => {
      disposed = true;
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }
      supabase.removeChannel(channel);
    };
  }, [dispatch, selectedOrderId]);

  useEffect(() => {
    if (!outletId) return undefined;

    let disposed = false;
    let refreshTimeout: number | null = null;
    const channelName = `running-order-created-${outletId}`;

    const queueRefresh = () => {
      if (disposed) return;
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }
      refreshTimeout = window.setTimeout(() => {
        dispatch(fetchTables(outletId));
        dispatch(fetchTakeAwayOrdersForOutlet(outletId));
        if (selectedTakeAwayId) {
          dispatch(refreshSelectedOrderContext());
        }
      }, 150);
    };

    // '*' (not just INSERT) so a settlement/status change or a
    // conflict-resolution delete on any order in the outlet also refreshes
    // the grid immediately, not just brand-new orders.
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `outlet_id=eq.${outletId}`,
        },
        queueRefresh,
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          queueRefresh();
        }
      });

    return () => {
      disposed = true;
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }
      supabase.removeChannel(channel);
    };
  }, [dispatch, outletId, selectedTakeAwayId]);

  return null;
};
